// Servicio de autenticación — gestiona el token y la sesión
import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import { StorageService } from './storage.service';
import { API_ENDPOINTS } from '../config/api.config';
import { environment } from '../../../environments/environment';

interface AuthToken {
  accessToken: string;
  sessionId?: string;
  expiresIn: number;
  issuedAt: number;
}

const TOKEN_STORAGE_KEY = 'sura_auth_token';
// Duración del token en milisegundos (60 minutos)
const TOKEN_DURATION_MS = 60 * 60 * 1000;
const MAX_TOKEN_ATTEMPTS = 5;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(StorageService);

  // Estado del token con Signal
  private readonly _token = signal<AuthToken | null>(this.loadStoredToken());
  private readonly _tokenAttempts = signal(0);
  readonly token = this._token.asReadonly();

  /** Verifica si el token actual es válido (no expirado) */
  isTokenValid(): boolean {
    const token = this._token();
    if (!token) return false;
    const now = Date.now();
    return now - token.issuedAt < TOKEN_DURATION_MS;
  }

  /** Obtiene el token de acceso para adjuntar en headers */
  getAccessToken(): string | null {
    return this._token()?.accessToken ?? null;
  }

  /** Obtiene el ID de sesión */
  getSessionId(): string | null {
    return this._token()?.sessionId ?? null;
  }

  /** Solicita un nuevo token al backend */
  fetchToken(): Observable<AuthToken> {
    if (this._tokenAttempts() >= MAX_TOKEN_ATTEMPTS) {
      return throwError(() => new Error('Se alcanzo el maximo de 5 intentos para obtener token.'));
    }

    this._tokenAttempts.update((attempts) => attempts + 1);

    return this.http
      .post<unknown>(API_ENDPOINTS.auth.token, this.buildTokenBody().toString(), {
        headers: this.buildTokenHeaders(),
      })
      .pipe(
        map((response) => this.normalizeTokenResponse(response)),
        tap((tokenData) => {
          this._token.set(tokenData);
          this.storage.setSession(TOKEN_STORAGE_KEY, tokenData);
          this.resetTokenAttempts();
        }),
        catchError((error) => throwError(() => error)),
      );
  }

  /** Renueva el token existente */
  refreshToken(): Observable<AuthToken> {
    return this.fetchToken();
  }

  /** Limpia el token al cerrar sesión */
  clearToken(): void {
    this._token.set(null);
    this.storage.removeSession(TOKEN_STORAGE_KEY);
    this.resetTokenAttempts();
  }

  /** Carga el token almacenado en sesión si existe y es válido */
  private loadStoredToken(): AuthToken | null {
    try {
      const stored = sessionStorage.getItem(TOKEN_STORAGE_KEY);
      if (!stored) return null;
      const token = JSON.parse(stored) as AuthToken;
      const now = Date.now();
      if (now - token.issuedAt >= TOKEN_DURATION_MS) return null;
      return token;
    } catch {
      return null;
    }
  }

  private buildTokenBody(): HttpParams {
    return new HttpParams()
      .set('client_id', environment.tokenClientId ?? '')
      .set('grant_type', environment.tokenGrantType ?? 'client_credentials')
      .set('scope', environment.tokenScope ?? '')
      .set('client_secret', environment.tokenClientSecret ?? '');
  }

  private buildTokenHeaders(): HttpHeaders {
    const jwtToken = environment.tokenJwt ?? '';
    const baseHeaders: Record<string, string> = {
      'Content-Type': 'application/x-www-form-urlencoded',
    };

    if (jwtToken.trim().length > 0) {
      baseHeaders['jwt'] = jwtToken;
      baseHeaders['Authorization'] = `Bearer ${jwtToken}`;
    }

    return new HttpHeaders(baseHeaders);
  }

  private normalizeTokenResponse(response: unknown): AuthToken {
    const payload = this.extractPayload(response);
    const accessToken =
      this.readString(payload, 'accessToken') ?? this.readString(payload, 'access_token') ?? '';
    const sessionId =
      this.readString(payload, 'sessionId') ?? this.readString(payload, 'session_id') ?? undefined;
    const expiresIn = this.readNumber(payload, 'expiresIn') ?? this.readNumber(payload, 'expires_in') ?? 3600;

    return {
      accessToken,
      sessionId,
      expiresIn,
      issuedAt: Date.now(),
    };
  }

  private extractPayload(response: unknown): Record<string, unknown> {
    if (!response || typeof response !== 'object') {
      return {};
    }

    const root = response as Record<string, unknown>;
    const nested = root['data'];
    if (nested && typeof nested === 'object') {
      return nested as Record<string, unknown>;
    }

    return root;
  }

  private readString(payload: Record<string, unknown>, key: string): string | null {
    const value = payload[key];
    return typeof value === 'string' && value.length > 0 ? value : null;
  }

  private readNumber(payload: Record<string, unknown>, key: string): number | null {
    const value = payload[key];
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'string' && value.trim().length > 0) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }

  private resetTokenAttempts(): void {
    this._tokenAttempts.set(0);
  }
}
